import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { CarsAvailabilitySelectors } from "@apac/cars";
import type { CreditCard } from "@b2bportal/card-api";
import { FlightShopSelectors } from "@b2bportal/core-flights";
import type { Promotion } from "@b2bportal/hsbc-promotions-api";
import { Product } from "@b2bportal/purchase-api";
import { selectCards } from "@hsbc/wallet";
import { HotelsShopSelectors } from "@tenant/hotels";
import { isCardApplicable, isPromotionApplicable } from "./applicability";
import { usePremierDestinationPromotion } from "./usePremierDestination";

interface UseIsPromotionApplicableReturn {
  currentPromotions: Array<Promotion>;
  hasPremierMastercard: boolean;
  isCardApplicableForProduct: (cards: CreditCard[]) => boolean;
  getCurrentPromotionForCard: (card?: CreditCard) => Promotion | undefined;
}

export const useIsPromotionApplicable = (
  product?: Product
): UseIsPromotionApplicableReturn => {
  const allCards = useSelector(selectCards);
  const hotel = useSelector(HotelsShopSelectors.getSelectedHotel);
  const hotelCountry = hotel?.lodging?.country;

  const destinationCountryCode = useSelector(
    FlightShopSelectors.getDestinationCountryCode
  );
  const originCountryCode = useSelector(
    FlightShopSelectors.getOriginCountryCode
  );

  const carsPickUpLocationCountry = useSelector(
    CarsAvailabilitySelectors.getPickUpLocationCountry
  );

  const {
    enabledPromotions: {
      hotels: isHotelsEnabled,
      flights: isFlightsEnabled,
      cars: isCarsEnabled,
    },
    promotions: {
      hotels: hotelsPremierDestinationPromo,
      flights: flightsPremierDestinationPromo,
      cars: carsPremierDestinationPromo,
    },
  } = usePremierDestinationPromotion();

  const isHotelsApplicable = useCallback(
    () =>
      isHotelsEnabled &&
      hotelsPremierDestinationPromo != null &&
      hotelsPremierDestinationPromo.some((promotion) =>
        isPromotionApplicable(promotion, {
          product,
          country: hotelCountry,
        })
      ),
    [hotelCountry, hotelsPremierDestinationPromo, isHotelsEnabled, product]
  );

  const isFlightsApplicable = useCallback(
    () =>
      isFlightsEnabled &&
      flightsPremierDestinationPromo != null &&
      flightsPremierDestinationPromo.some(
        (promotion) =>
          isPromotionApplicable(promotion, {
            product,
            country: originCountryCode,
          }) ||
          isPromotionApplicable(promotion, {
            product,
            country: destinationCountryCode,
          })
      ),
    [
      isFlightsEnabled,
      flightsPremierDestinationPromo,
      product,
      originCountryCode,
      destinationCountryCode,
    ]
  );

  const isCarsApplicable = useCallback(
    () =>
      isCarsEnabled &&
      carsPremierDestinationPromo != null &&
      carsPremierDestinationPromo.some((promotion) =>
        isPromotionApplicable(promotion, {
          product,
          country: carsPickUpLocationCountry,
        })
      ),
    [
      carsPickUpLocationCountry,
      carsPremierDestinationPromo,
      isCarsEnabled,
      product,
    ]
  );

  const isApplicable = useMemo(() => {
    if (product === Product.Hotel) {
      return isHotelsEnabled && isHotelsApplicable();
    }
    if (product === Product.Flight) {
      return isFlightsEnabled && isFlightsApplicable();
    }
    if (product === Product.Ground) {
      return isCarsEnabled && isCarsApplicable();
    }
    return false;
  }, [
    product,
    isHotelsEnabled,
    isHotelsApplicable,
    isFlightsEnabled,
    isFlightsApplicable,
    isCarsEnabled,
    isCarsApplicable,
  ]);

  const currentPromotions = useMemo(() => {
    if (product === Product.Hotel) {
      return hotelsPremierDestinationPromo;
    }
    if (product === Product.Flight) {
      return flightsPremierDestinationPromo;
    }
    if (product === Product.Ground) {
      return carsPremierDestinationPromo;
    }
    return [];
  }, [
    product,
    hotelsPremierDestinationPromo,
    flightsPremierDestinationPromo,
    carsPremierDestinationPromo,
  ]);

  const isCardApplicableForProduct = useCallback(
    (cards) =>
      currentPromotions.some((promotion) => isCardApplicable(promotion, cards)),
    [currentPromotions]
  );

  const getCurrentPromotionForCard = useCallback(
    (card) => {
      if (card != null) {
        const selectedPromotion = currentPromotions.find((promotion) =>
          isCardApplicable(promotion, [card])
        );

        return selectedPromotion != null
          ? selectedPromotion
          : currentPromotions[0];
      }
    },
    [currentPromotions]
  );

  const hasPremierMastercard = useMemo(
    () =>
      allCards.length > 0 &&
      isApplicable &&
      isCardApplicableForProduct(allCards),
    [allCards, isApplicable, isCardApplicableForProduct]
  );

  return {
    currentPromotions,
    hasPremierMastercard,
    isCardApplicableForProduct,
    getCurrentPromotionForCard,
  };
};
